import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import React from 'react';

type State = Readonly<{
  /**
   * Drawer in open or closed mode
   */
  open: boolean;
  /**
   * Navigation column visibility (independent of org column)
   */
  navColumnOpen: boolean;
  /**
   * Mobile drawer open state (for temporary drawer on small screens)
   */
  mobileOpen: boolean;
  /**
   * Route stack
   * Latest route at the front of the array
   */
  routeStack: string[];
  /**
   * current route
   */
  currentRoute?: string;
  /**
   * navigation direction
   * - if true, forward navigation (i.e. menu slides from right)
   * - if false, goBack navigation (i.e. menu slides from left)
   */
  goForward: boolean;
}>;

const initialState: State = {
  open: true,
  navColumnOpen: true,
  mobileOpen: false,
  routeStack: [],
  currentRoute: undefined,
  goForward: true,
};

const leftNavSlice = createSlice({
  name: 'leftNav',
  initialState,
  reducers: {
    /**
     * Navigate forward to a specific route
     */
    jumpTo: (state, { payload }: PayloadAction<string>) => {
      if (payload !== state.currentRoute) {
        state.goForward = true;
        state.currentRoute = payload;
        state.routeStack.unshift(payload);
      }
    },
    /**
     * Navigate backward to originating route
     */
    goBack: (state) => {
      state.goForward = false;
      state.routeStack.shift();
      const currentRoute = state.routeStack[0];
      if (currentRoute) {
        state.currentRoute = currentRoute;
      }
    },
    /**
     * Set state of drawer
     */
    setOpen: (state, { payload }: PayloadAction<boolean>) => {
      state.open = payload;
    },
    /**
     * Set state of navigation column
     */
    setNavColumnOpen: (state, { payload }: PayloadAction<boolean>) => {
      state.navColumnOpen = payload;
    },
    /**
     * Set mobile drawer open state (for temporary drawer on small screens)
     */
    setMobileOpen: (state, { payload }: PayloadAction<boolean>) => {
      state.mobileOpen = payload;
    },
    /**
     * Toggle mobile drawer open state
     */
    toggleMobileOpen: (state) => {
      state.mobileOpen = !state.mobileOpen;
    },
  },
});

// Helper type to replace the return type of action creator
type ReplaceReturnType<T extends (...a: any) => any, TNewReturn> = (
  ...a: Parameters<T>
) => TNewReturn;

const { actions } = leftNavSlice;

interface ContextT {
  state: State;
  // The following actions are safe to be used from appContext directly
  jumpTo: ReplaceReturnType<typeof actions.jumpTo, void>;
  goBack: ReplaceReturnType<typeof actions.goBack, void>;
  setOpen: ReplaceReturnType<typeof actions.setOpen, void>;
  setNavColumnOpen: ReplaceReturnType<typeof actions.setNavColumnOpen, void>;
  setMobileOpen: ReplaceReturnType<typeof actions.setMobileOpen, void>;
  toggleMobileOpen: ReplaceReturnType<typeof actions.toggleMobileOpen, void>;
}

// @ts-expect-error: intentionally leaving default value to be empty
const Context = React.createContext<ContextT>();

interface Props {
  children: React.ReactNode;
}

export function LeftNavContextProvider(props: Props) {
  const [state, dispatch] = React.useReducer(
    leftNavSlice.reducer,
    initialState
  );

  const actionList: Omit<ContextT, 'state'> = {
    jumpTo: (v) => dispatch(actions.jumpTo(v)),
    goBack: () => dispatch(actions.goBack()),
    setOpen: (v) => dispatch(actions.setOpen(v)),
    setNavColumnOpen: (v) => dispatch(actions.setNavColumnOpen(v)),
    setMobileOpen: (v) => dispatch(actions.setMobileOpen(v)),
    toggleMobileOpen: () => dispatch(actions.toggleMobileOpen()),
  };

  return <Context.Provider value={{ state, ...actionList }} {...props} />;
}

export default function useLeftNavContext() {
  const context = React.useContext(Context);
  if (!context) {
    throw new Error(
      `useLeftNavContext must be used within a LeftNavContextProvider`
    );
  }
  return context;
}
